import React from "react";
import Box from "@mui/material/Box";
import { Button, CircularProgress, Tooltip, Typography } from "@mui/material";
import { useNotificationProvider } from "hooks";
import { useSelector } from "react-redux";
import { selectUserInfo } from "redux/userInfo/userInfoSlice";
import { useActiveVehicle } from "containers/pages/CANpark/context/ActiveVehicleProvider";
import { Ticket } from "containers/pages/CANpark/types/ticket";
import { submitTicket } from "containers/pages/CANpark/utils/submitTicket";
import TicketRecord from "../../ticketRecord/TicketRecord";
import ScannedPlateHeader from "../../scannedPlateHeader/ScannedPlateHeader";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import AddIcon from '@mui/icons-material/Add';

interface ActiveVehicleDrawerProps {}

const ActiveVehicleDrawerConfirmationStage: React.FC<
  ActiveVehicleDrawerProps
> = ({}) => {
  const {
    ticketFormMethods,
    closeDrawer,
    currentSearch,
    setStage,
    goBack,
    parkingLocation,
  } = useActiveVehicle();
  const { notifySuccess, notifyError } = useNotificationProvider();
  const { userInfo } = useSelector(selectUserInfo);
  const [loading, setLoading] = React.useState(false);

  const onSubmit = async (data: Ticket) => {
    setLoading(true);
    try {
      await submitTicket(data, {
        userId: userInfo.userId,
        parkingLocation,
        notifySuccess,
        notifyError,
        resetForm: () => ticketFormMethods.reset(),
        setStage,
        closeDrawer,
      });
    } finally {
      setLoading(false);
    }
  };

  if (!currentSearch)
    throw new Error(
      "No search data found in ActiveVehicleDrawerConfirmationStage"
    );

  return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 2,
            px: 2,
            pb: 2,
            maxWidth: 600,
            width: "100%",
            flexDirection: "column",
          }}
        >
          <Typography variant="h6" fontWeight={500} color="primary">
            Confirm Record
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review the details below, then save the record.
          </Typography>
          <ScannedPlateHeader
            variant="banner"
            licenseNumber={ticketFormMethods.getValues("licenseNumber") ?? ""}
            licenseState={ticketFormMethods.getValues("licenseState") ?? ""}
          />
          <TicketRecord
            ticket={ticketFormMethods.getValues()}
            showContentOnLoad={true}
            parkingLocation={currentSearch?.parkingLocation}
          />
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
            flexWrap="wrap"
          >
            <Button
              variant="outlined"
              color="primary"
              onClick={goBack}
              startIcon={<ArrowBackIosNewIcon />}
              disabled={loading}
            >
              Back
            </Button>
            <Box display="flex" gap={2} sx={{ ml: "auto" }}>
              <Tooltip title={loading ? "Submitting..." : ""}>
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    data-tour="ticket-confirm"
                    onClick={ticketFormMethods.handleSubmit(onSubmit)}
                    disabled={loading}
                    endIcon={
                      loading ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <AddIcon />
                      )
                    }
                    sx={{
                      borderRadius: 2,
                      fontWeight: 500,
                      textTransform: "none",
                    }}
                  >
                    Save
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Box>
  );
};

export default ActiveVehicleDrawerConfirmationStage;
